import { memo, useMemo } from 'react';
import { useQuery } from 'data-fetching-client';
import partial from 'transmute/partial';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ActionCardComponents from 'automation-ui-canvas/action-features/ActionCardComponents';
import { useGetIsUngated } from 'automation-ui-core/hooks/authHooks';
import AutomationBuilderCard from 'automation-ui-flow-core/canvas/AutomationBuilderCard';
import { getActionHeaderTitle, getObjectTypeId } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import { INVALID_ACTION_TYPE } from 'automation-ui-flow-core/validation/FlowValidationErrorTypes';
import emptyFunction from 'react-utils/emptyFunction';
import { AUTOMATION_EXTENSION } from 'automation-ui-action-features/constants/ActionTypes';
import EditActionButtons from 'automation-ui-canvas/components/action-card-options/EditActionButtons';
import { getActionDefinition, GET_ACTION_DEFINITION } from 'automation-ui-action-features/actions/extension/extensionQueries';
import getExtensionAccessFromDefinition from 'automation-ui-action-features/actions/extension/utils/getExtensionAccessFromDefinition';
import ObjectsInActionCountButton from 'automation-ui-canvas/action-features/objects-in-action/ObjectsInActionCountButton';
import ActionDetailsButton from 'automation-ui-action-features/action-details/ActionDetailsButton';
import CommentBadge from 'automation-ui-canvas/action-features/commenting/CommentBadge';
import hasExplainerForAction from 'automation-ui-action-features/action-details/utils/hasExplainerForAction';
import ActionDetailsPopover from 'automation-ui-action-features/action-details/ActionDetailsPopover';
import { getActionIconAndColor } from 'automation-ui-action-features/utils/actionThemeUtils';
import ErrorBoundary from 'automation-ui-canvas/components/errors/ErrorBoundary';
import SunsettedActionCard from 'automation-ui-action-features/actions/sunsetted-action/SunsettedActionCard';
import { useExtensionDefinitionsInFlow } from 'automation-ui-canvas/hooks/useExtensionDefinitionsInFlow';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * By extracting this component, we can take advantage
 * of the shallow comparison that React.memo() does
 * to avoid unecessary updates when working with
 * large workflows.
 */
const FlowActionNode = ({
  clickable = true,
  nodeId,
  action,
  flow,
  flowValidation,
  isSelected,
  onSelectAction = emptyFunction,
  isHighlighted,
  flowId,
  readOnly,
  isCanvasVirtualRenderingDisabled,
  isDisabled,
  disabledDescription,
  objectsInActions,
  getActionOutputsForTargetActionById,
  width,
  height,
  showIcon,
  showLoadingOverlay = () => {},
  borderStyle,
  bannerText,
  overrideTitleAction,
  overrideAccentColor,
  layout
}) => {
  const isUngated = useGetIsUngated();
  const flowObjectTypeId = getObjectTypeId(flow);
  const accessibleExtensionDefinitions = useExtensionDefinitionsInFlow(flow);
  const {
    data: extensionDefinition
  } = useQuery(GET_ACTION_DEFINITION, {
    skip: action.actionType !== AUTOMATION_EXTENSION || !!action.actionTypeId && !!accessibleExtensionDefinitions[action.actionTypeId],
    variables: {
      id: action.actionTypeId
    }
  });
  const actionDefinition = getActionDefinition(extensionDefinition);
  const extensionAccess = getExtensionAccessFromDefinition(actionDefinition);
  const extensionDefinitions = Object.assign({}, action.actionTypeId && extensionAccess && {
    [action.actionTypeId]: extensionAccess
  }, accessibleExtensionDefinitions);
  const getHeaderTitle = getActionHeaderTitle({
    flow,
    extensionDefinitions,
    isUngated
  });
  const ActionCardComponent = action && ActionCardComponents[action.actionType];
  const actionId = action.getActionId();
  const actionValidation = flowValidation ? flowValidation.getErrorsByActionId(actionId) : null;
  const isInvalidActionError = !!actionValidation && !!actionValidation.first() && actionValidation.first().flowValidationErrorType === INVALID_ACTION_TYPE;
  const {
    iconName,
    iconUrl,
    theme
  } = getActionIconAndColor({
    action,
    extensionDefinitions
  });
  const showActionButtons = !isDisabled && !readOnly;
  const titleAction = useMemo(() => () => {
    const ActionDetailsButtonContent = () => /*#__PURE__*/_jsx(ActionDetailsPopover, {
      action: action,
      getHeaderTitle: getHeaderTitle,
      validationErrors: actionValidation,
      headerIconName: iconName,
      headerIconUrl: iconUrl,
      theme: theme,
      flowObjectTypeId: flowObjectTypeId,
      getActionOutputsForTargetActionById: getActionOutputsForTargetActionById
    });
    const showDetailsButton = hasExplainerForAction(action);
    const actionButtons = showActionButtons && /*#__PURE__*/_jsx(EditActionButtons, {
      flowId: flowId,
      action: action,
      flow: flow,
      getActionOutputsForTargetActionById: getActionOutputsForTargetActionById,
      flowObjectTypeId: flowObjectTypeId,
      onlyShowDelete: isInvalidActionError,
      showLoadingOverlay: showLoadingOverlay
    });
    const detailsButton = showDetailsButton && /*#__PURE__*/_jsx(ActionDetailsButton, {
      use: layout === 'zoomed-out' ? 'icon' : 'link',
      "data-test-id": `card-details-toggle-${nodeId}`
      // eslint-disable-next-line react/prop-types
      ,
      trackingIdentifier: action.actionType,
      Content: ActionDetailsButtonContent
    });
    // Always render something before the details button so it gets pushed to the right
    const separator = !actionButtons && detailsButton && /*#__PURE__*/_jsx("div", {});
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [actionButtons, separator, detailsButton]
    });
  }, [isInvalidActionError, flowId, nodeId, action, flow, flowObjectTypeId, getActionOutputsForTargetActionById, showLoadingOverlay, getHeaderTitle, actionValidation, iconName, iconUrl, theme, showActionButtons, layout]);
  if (!ActionCardComponent) {
    if (isInvalidActionError) {
      return /*#__PURE__*/_jsx(SunsettedActionCard, {
        clickable: clickable,
        getHeaderTitle: getHeaderTitle,
        actionId: actionId,
        isSelected: isSelected,
        isHighlighted: isHighlighted,
        statusType: "error",
        statusText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-flow-core.action-card.status.needsChanges"
        }),
        isDisabled: isDisabled,
        TitleAction: overrideTitleAction || titleAction,
        onClick: onSelectAction && partial(onSelectAction, nodeId),
        layout: layout
      });
    }
    return null;
  }
  const objectsInThisAction = objectsInActions ? objectsInActions.find(delayedAction => delayedAction.actionId === actionId) : null;
  const objectsInThisActionCount = objectsInThisAction && objectsInThisAction.count;
  let statusType;
  let statusMessage;
  if (actionValidation !== null && actionValidation !== void 0 && actionValidation.size) {
    statusType = 'error';
    statusMessage = 'automation-ui-flow-core.action-card.status.needsChanges';
  }
  const objectsInActionButton = objectsInThisActionCount && /*#__PURE__*/_jsx(ObjectsInActionCountButton, {
    actionId: actionId,
    flowObjectTypeId: flowObjectTypeId,
    onSelectAction: onSelectAction,
    count: objectsInThisActionCount
  });
  return /*#__PURE__*/_jsx(CommentBadge, {
    nodeId: nodeId,
    actionType: action.actionType,
    actionMetadata: action.getMetadata(),
    readOnly: readOnly,
    children: /*#__PURE__*/_jsx(ErrorBoundary, {
      boundaryName: `ActionCardComponent_${action.actionType}`,
      renderError: () => /*#__PURE__*/_jsx(AutomationBuilderCard, {
        headerTitle: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "fatalErrors.errorLoadingActionCard.title"
        }),
        content: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "fatalErrors.errorLoadingActionCard.body"
        }),
        headerIcon: "exclamation",
        titleUseTheme: "norman"
      }),
      children: /*#__PURE__*/_jsx(ActionCardComponent, {
        clickable: clickable,
        flow: flow,
        getHeaderTitle: getHeaderTitle,
        getActionOutputsForTargetActionById: getActionOutputsForTargetActionById,
        flowId: flowId,
        flowObjectTypeId: flowObjectTypeId,
        action: action,
        actionId: actionId,
        actionMetadata: action.getMetadata(),
        isSelected: isSelected,
        isHighlighted: isHighlighted,
        onSelectAction: onSelectAction,
        onClick: onSelectAction && partial(onSelectAction, nodeId),
        readOnly: readOnly,
        disabledDescription: disabledDescription,
        isDisabled: isDisabled,
        TitleAction: overrideTitleAction || titleAction,
        width: width,
        height: height,
        showIcon: showIcon,
        headerIcon: iconName
        // We can't draw externally-hosted images from integrators onto a canvas for image export
        ,
        headerImage: isCanvasVirtualRenderingDisabled ? '' : iconUrl,
        titleUseTheme: theme,
        statusType: statusType,
        statusText: objectsInThisActionCount ? objectsInActionButton : statusMessage && /*#__PURE__*/_jsx(FormattedMessage, {
          message: statusMessage
        }),
        borderStyle: borderStyle,
        bannerText: bannerText,
        overrideAccentColor: overrideAccentColor,
        layout: layout
      })
    })
  });
};
export default /*#__PURE__*/memo(FlowActionNode);